'use client';

import { useEffect } from 'react';
import { handleDeploymentErrorReload } from '@/utils/deploymentErrorRecovery';
import { isSalesforceEmbedFrame } from '@/utils/salesforceEmbed';

/**
 * Global Error Boundary for Next.js App Router
 *
 * This component catches errors at the root level, including:
 * - Server Action failures ("Failed to find Server Action")
 * - ChunkLoadError (stale JS bundles after deployment)
 * - Root layout rendering errors
 *
 * For deployment-related errors, it automatically reloads the page (deferred if a chat
 * send is in-flight -- see utils/deploymentErrorRecovery.ts). For other errors, it
 * provides a manual retry option.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error('Global error caught:', error);
    handleDeploymentErrorReload(error.message || '');
  }, [error]);

  const salesforceEmbed = isSalesforceEmbedFrame();

  const handleReload = () => {
    // Clear stored build ID before reload
    try {
      localStorage.removeItem('mb_build_id');
    } catch {
      // Ignore storage errors
    }
    window.location.reload();
  };

  return (
    <html lang="en">
      <body>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            padding: '20px',
            fontFamily: 'system-ui, -apple-system, sans-serif',
            backgroundColor: '#f9fafb',
            color: '#111827',
          }}
        >
          <div
            style={{
              maxWidth: '400px',
              textAlign: 'center',
            }}
          >
            <h1
              style={{
                fontSize: '24px',
                fontWeight: 600,
                marginBottom: '12px',
              }}
            >
              Something went wrong
            </h1>
            <p
              style={{
                fontSize: '14px',
                color: '#6b7280',
                marginBottom: '24px',
              }}
            >
              We encountered an unexpected error. This might be due to a recent
              update. {salesforceEmbed ? 'Please reload the page in Salesforce.' : 'Please try refreshing the page.'}
            </p>
            <div
              style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'center',
              }}
            >
              <button
                onClick={reset}
                style={{
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#374151',
                  backgroundColor: '#ffffff',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: 'pointer',
                }}
              >
                Try Again
              </button>
              {/* A Salesforce embed cannot sign itself back in; only reloading the Salesforce page can. */}
              {!salesforceEmbed && (
                <button
                  onClick={handleReload}
                  style={{
                    padding: '10px 20px',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#ffffff',
                    backgroundColor: '#2563eb',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                  }}
                >
                  Refresh Page
                </button>
              )}
            </div>
            {process.env.NODE_ENV === 'development' && (
              <details
                style={{
                  marginTop: '24px',
                  textAlign: 'left',
                  fontSize: '12px',
                  color: '#6b7280',
                }}
              >
                <summary style={{ cursor: 'pointer', marginBottom: '8px' }}>
                  Error Details
                </summary>
                <pre
                  style={{
                    backgroundColor: '#1f2937',
                    color: '#f3f4f6',
                    padding: '12px',
                    borderRadius: '6px',
                    overflow: 'auto',
                    maxHeight: '200px',
                  }}
                >
                  {error.message}
                  {error.stack && `\n\n${error.stack}`}
                  {error.digest && `\n\nDigest: ${error.digest}`}
                </pre>
              </details>
            )}
          </div>
        </div>
      </body>
    </html>
  );
}
